import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBrandLogo } from '../hooks/useBrandLogo';
import axios from 'axios';
import { toast } from 'sonner';
import MobileHeader from '../components/MobileHeader';
import {
  FileSpreadsheet,
  Download,
  Users,
  UserCheck,
  FileText,
  Ruler,
  Heart,
  Receipt,
  Briefcase,
  ShoppingCart,
  CreditCard,
  Filter,
  Calendar,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Loader2,
  LogOut,
  ArrowLeft,
  Database
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Configuración de módulos
const MODULOS = [
  {
    id: 'crm',
    nombre: 'CRM / Prospectos',
    descripcion: 'Todos los prospectos registrados en CRM',
    icono: Users,
    color: 'bg-blue-500',
    colorLight: 'bg-blue-50',
    endpoint: '/export/modulo/crm',
    filtros: ['fecha', 'estado', 'vendedor']
  },
  {
    id: 'clientes',
    nombre: 'Clientes',
    descripcion: 'Base maestra de clientes',
    icono: UserCheck,
    color: 'bg-emerald-500',
    colorLight: 'bg-emerald-50',
    endpoint: '/export/modulo/clientes',
    filtros: ['fecha']
  },
  {
    id: 'cotizaciones',
    nombre: 'Cotizaciones',
    descripcion: 'Todas las cotizaciones (con o sin ficha)',
    icono: FileText,
    color: 'bg-purple-500',
    colorLight: 'bg-purple-50',
    endpoint: '/export/modulo/cotizaciones',
    filtros: ['fecha', 'estado', 'vendedor', 'moneda']
  },
  {
    id: 'fichas',
    nombre: 'Fichas Técnicas',
    descripcion: 'Todas las fichas de medidas',
    icono: Ruler,
    color: 'bg-amber-500',
    colorLight: 'bg-amber-50',
    endpoint: '/export/modulo/fichas',
    filtros: ['fecha', 'estado', 'tipo_prenda']
  },
  {
    id: 'aliados',
    nombre: 'Aliados / Referidos',
    descripcion: 'Aliados y referidos con comisiones',
    icono: Heart,
    color: 'bg-pink-500',
    colorLight: 'bg-pink-50',
    endpoint: '/export/modulo/aliados-referidos',
    filtros: ['fecha', 'estado', 'aliado', 'tipo_referido']
  },
  {
    id: 'cuentas_cobro',
    nombre: 'Cuentas de Cobro',
    descripcion: 'Cuentas de cobro generadas',
    icono: Receipt,
    color: 'bg-orange-500',
    colorLight: 'bg-orange-50',
    endpoint: '/export/modulo/cuentas-cobro',
    filtros: ['fecha', 'estado', 'aliado']
  },
  {
    id: 'vendedores',
    nombre: 'Vendedores',
    descripcion: 'Equipo comercial',
    icono: Briefcase,
    color: 'bg-indigo-500',
    colorLight: 'bg-indigo-50',
    endpoint: '/export/modulo/vendedores',
    filtros: ['estado']
  },
  {
    id: 'ventas',
    nombre: 'Ventas / Pedidos',
    descripcion: 'Cotizaciones convertidas en venta',
    icono: ShoppingCart,
    color: 'bg-green-600',
    colorLight: 'bg-green-50',
    endpoint: '/export/modulo/ventas',
    filtros: ['fecha', 'vendedor']
  },
  {
    id: 'pagos',
    nombre: 'Pagos / Cuotas',
    descripcion: 'Planes de pago y cuotas',
    icono: CreditCard,
    color: 'bg-cyan-500',
    colorLight: 'bg-cyan-50',
    endpoint: '/export/modulo/pagos',
    filtros: ['fecha', 'estado']
  }
];

// Estados disponibles por módulo
const ESTADOS_POR_MODULO = {
  crm: [
    { value: '', label: 'Todos' },
    { value: 'nuevo', label: 'Nuevo' },
    { value: 'contactado', label: 'Contactado' },
    { value: 'en_seguimiento', label: 'En seguimiento' },
    { value: 'cotizado', label: 'Cotizado' },
    { value: 'vendido', label: 'Vendido' },
    { value: 'no_interesado', label: 'No interesado' }
  ],
  cotizaciones: [
    { value: '', label: 'Todos' },
    { value: 'borrador', label: 'Borrador' },
    { value: 'enviada', label: 'Enviada' },
    { value: 'aprobada', label: 'Aprobada' },
    { value: 'rechazada', label: 'Rechazada' }
  ],
  fichas: [
    { value: '', label: 'Todos' },
    { value: 'borrador', label: 'Borrador' },
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'en_corte', label: 'En corte' },
    { value: 'en_confeccion', label: 'En confección' },
    { value: 'finalizada', label: 'Finalizada' }
  ],
  aliados: [
    { value: '', label: 'Todos' },
    { value: 'registrado', label: 'Registrado' },
    { value: 'contactado', label: 'Contactado' },
    { value: 'cotizado', label: 'Cotizado' },
    { value: 'venta_realizada', label: 'Venta realizada' },
    { value: 'pagado', label: 'Pagado' }
  ],
  cuentas_cobro: [
    { value: '', label: 'Todos' },
    { value: 'emitida', label: 'Emitida' },
    { value: 'pagada', label: 'Pagada' },
    { value: 'anulada', label: 'Anulada' }
  ],
  vendedores: [
    { value: '', label: 'Todos' },
    { value: 'activo', label: 'Activo' },
    { value: 'inactivo', label: 'Inactivo' }
  ],
  pagos: [
    { value: '', label: 'Todos' },
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'pagada', label: 'Pagada' },
    { value: 'vencida', label: 'Vencida' }
  ]
};

const TIPOS_PRENDA = [
  { value: '', label: 'Todos' },
  { value: 'camisa', label: 'Camisa' },
  { value: 'saco', label: 'Saco' },
  { value: 'pantalon', label: 'Pantalón' },
  { value: 'chaleco', label: 'Chaleco' },
  { value: 'smoking', label: 'Smoking' }
];

const TIPOS_REFERIDO = [
  { value: '', label: 'Todos' },
  { value: 'novio', label: 'Novio' },
  { value: 'novia', label: 'Novia' },
  { value: 'papa_novio', label: 'Papá Novio' },
  { value: 'papa_novia', label: 'Papá Novia' },
  { value: 'invitado', label: 'Invitado' },
  { value: 'mascota', label: 'Mascota' },
  { value: 'paje', label: 'Paje' },
  { value: 'corte_completa', label: 'Corte Completa' }
];

const MONEDAS = [
  { value: '', label: 'Todas' },
  { value: 'COP', label: 'COP - Peso Colombiano' },
  { value: 'USD', label: 'USD - Dólar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'MXN', label: 'MXN - Peso Mexicano' }
];

const ExportModules = () => {
  const navigate = useNavigate();
  const { user, logout, isGerencia, isAdmin } = useAuth();
  const { logoUrl } = useBrandLogo();
  
  const [counts, setCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    estado: '',
    vendedor_id: '',
    aliado_id: '',
    tipo_prenda: '',
    tipo_referido: '',
    moneda: ''
  });
  
  // Datos para filtros
  const [vendedores, setVendedores] = useState([]);
  const [aliados, setAliados] = useState([]);

  useEffect(() => {
    if (!isGerencia() && !isAdmin()) {
      navigate('/dashboard');
      toast.error('Acceso restringido a Admin y Gerencia');
      return;
    }
    fetchCounts();
    fetchFilterData();
  }, [isGerencia, isAdmin, navigate]);

  const fetchCounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/export/modulo/conteo`);
      setCounts(response.data);
    } catch (error) {
      console.debug('Error loading counts:', error);
    } finally {
      setLoadingCounts(false);
    }
  };

  const fetchFilterData = async () => {
    try {
      const [vendedoresRes, aliadosRes] = await Promise.all([
        axios.get(`${API_URL}/vendedores`).catch(() => ({ data: { vendedores: [] } })),
        axios.get(`${API_URL}/aliados`).catch(() => ({ data: { aliados: [] } }))
      ]);
      setVendedores(vendedoresRes.data?.vendedores || []);
      setAliados(aliadosRes.data?.aliados || []);
    } catch (error) {
      console.debug('Error loading filter data');
    }
  };

  const handleDownload = async (modulo) => {
    setDownloading(modulo.id);
    
    try {
      // Construir query params basados en los filtros del módulo
      const params = new URLSearchParams();
      
      if (filtros.fecha_inicio && modulo.filtros.includes('fecha')) {
        params.append('fecha_inicio', filtros.fecha_inicio);
      }
      if (filtros.fecha_fin && modulo.filtros.includes('fecha')) {
        params.append('fecha_fin', filtros.fecha_fin);
      }
      if (filtros.estado && modulo.filtros.includes('estado')) {
        params.append('estado', filtros.estado);
      }
      if (filtros.vendedor_id && modulo.filtros.includes('vendedor')) {
        params.append('vendedor_id', filtros.vendedor_id);
      }
      if (filtros.aliado_id && modulo.filtros.includes('aliado')) {
        params.append('aliado_id', filtros.aliado_id);
      }
      if (filtros.tipo_prenda && modulo.filtros.includes('tipo_prenda')) {
        params.append('tipo_prenda', filtros.tipo_prenda);
      }
      if (filtros.tipo_referido && modulo.filtros.includes('tipo_referido')) {
        params.append('tipo_referido', filtros.tipo_referido);
      }
      if (filtros.moneda && modulo.filtros.includes('moneda')) {
        params.append('moneda', filtros.moneda);
      }
      
      const url = `${API_URL}${modulo.endpoint}${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });

      // Crear enlace de descarga
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      
      // Obtener nombre del archivo
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${modulo.id}_${new Date().toISOString().slice(0,10)}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      toast.success(`${modulo.nombre} descargado exitosamente`);
      
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No hay registros disponibles con los filtros seleccionados');
      } else {
        toast.error(error.response?.data?.detail || `Error al descargar ${modulo.nombre}`);
      }
    } finally {
      setDownloading(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const resetFiltros = () => {
    setFiltros({
      fecha_inicio: '',
      fecha_fin: '',
      estado: '',
      vendedor_id: '',
      aliado_id: '',
      tipo_prenda: '',
      tipo_referido: '',
      moneda: ''
    });
  };

  const getModuloCount = (modulo) => {
    if (modulo.id === 'aliados') {
      return (counts.aliados || 0) + (counts.referidos || 0);
    }
    return counts[modulo.id] || 0;
  };

  const renderFiltrosModulo = (modulo) => {
    const { filtros: filtrosModulo } = modulo;
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 p-4 bg-zinc-50 rounded-lg">
        {filtrosModulo.includes('fecha') && (
          <>
            <div>
              <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
                Fecha Inicio
              </label>
              <input
                type="date"
                value={filtros.fecha_inicio}
                onChange={(e) => setFiltros(prev => ({ ...prev, fecha_inicio: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
                Fecha Fin
              </label>
              <input
                type="date"
                value={filtros.fecha_fin}
                onChange={(e) => setFiltros(prev => ({ ...prev, fecha_fin: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </>
        )}
        
        {filtrosModulo.includes('estado') && ESTADOS_POR_MODULO[modulo.id] && (
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Estado
            </label>
            <select
              value={filtros.estado}
              onChange={(e) => setFiltros(prev => ({ ...prev, estado: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
            >
              {ESTADOS_POR_MODULO[modulo.id].map(e => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
          </div>
        )}
        
        {filtrosModulo.includes('vendedor') && (
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Vendedor
            </label>
            <select
              value={filtros.vendedor_id}
              onChange={(e) => setFiltros(prev => ({ ...prev, vendedor_id: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
            >
              <option value="">Todos</option>
              {vendedores.map(v => (
                <option key={v.id} value={v.id}>{v.codigo} - {v.nombre}</option>
              ))}
            </select>
          </div>
        )}
        
        {filtrosModulo.includes('aliado') && (
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Aliado
            </label>
            <select
              value={filtros.aliado_id}
              onChange={(e) => setFiltros(prev => ({ ...prev, aliado_id: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
            >
              <option value="">Todos</option>
              {aliados.map(a => (
                <option key={a.id} value={a.id}>{a.nombre}</option>
              ))}
            </select>
          </div>
        )}
        
        {filtrosModulo.includes('tipo_prenda') && (
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Tipo Prenda
            </label>
            <select
              value={filtros.tipo_prenda}
              onChange={(e) => setFiltros(prev => ({ ...prev, tipo_prenda: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
            >
              {TIPOS_PRENDA.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        )}
        
        {filtrosModulo.includes('tipo_referido') && (
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Tipo Referido
            </label>
            <select
              value={filtros.tipo_referido}
              onChange={(e) => setFiltros(prev => ({ ...prev, tipo_referido: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
            >
              {TIPOS_REFERIDO.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        )}
        
        {filtrosModulo.includes('moneda') && (
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Moneda
            </label>
            <select
              value={filtros.moneda}
              onChange={(e) => setFiltros(prev => ({ ...prev, moneda: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
            >
              {MONEDAS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        )}
        
        <div className="flex items-end gap-2">
          <button
            onClick={resetFiltros}
            className="px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-black border border-border rounded-lg hover:bg-zinc-100 transition-colors"
            data-testid={`clear-filters-${modulo.id}`}
          >
            Limpiar
          </button>
          <button
            onClick={() => handleDownload(modulo)}
            disabled={downloading === modulo.id}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 ${modulo.color} text-white rounded-lg text-xs uppercase tracking-wider font-medium hover:opacity-90 transition-all disabled:opacity-50`}
            data-testid={`download-${modulo.id}`}
          >
            {downloading === modulo.id ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Descargando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Descargar Excel
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <MobileHeader title="Exportar por Módulo" />
      
      {/* Desktop Header */}
      <header className="bg-black sticky top-0 z-20 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-18 py-4">
            <div className="flex items-center gap-5">
              {logoUrl && (
                <img src={logoUrl} alt="Logo" className="h-11 w-auto object-contain brightness-0 invert" />
              )}
              <div>
                <h1 className="font-serif text-xl text-white tracking-wide">ATELIER <span className="text-white/40">—</span> JPM</h1>
                <p className="text-[9px] uppercase tracking-[0.15em] text-white/50">Exportar por Módulo</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => navigate('/export/executive')} 
                className="px-4 py-2 text-[10px] uppercase tracking-wider text-gold hover:text-gold-light hover:bg-gold/10 transition-all rounded-lg"
              >
                Exportación Ejecutiva
              </button>
              <button 
                onClick={() => navigate('/dashboard')} 
                className="px-4 py-2 text-[10px] uppercase tracking-wider text-white/70 hover:text-white hover:bg-white/10 transition-all rounded-lg"
              >
                Dashboard
              </button>
              <button 
                onClick={handleLogout} 
                className="px-3 py-2 text-white/40 hover:text-white hover:bg-white/10 transition-all rounded-lg"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Título */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center">
              <Database className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-serif text-2xl md:text-3xl text-black">Exportar por Módulo</h1>
              <p className="text-sm text-muted-foreground">Descarga la base de datos de cada módulo de forma independiente</p>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8 flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">¿Cómo funciona?</p>
            <p>Cada módulo exporta sus propios registros de forma independiente. Un cliente puede existir solo en CRM, solo en Fichas, o en varios módulos a la vez. Selecciona el módulo que necesitas y aplica filtros antes de descargar.</p>
          </div>
        </div>

        {/* Grid de Módulos */}
        <div className="space-y-4" data-testid="modules-grid">
          {MODULOS.map((modulo) => {
            const Icono = modulo.icono;
            const isExpanded = expandedModule === modulo.id;
            const count = getModuloCount(modulo);
            
            return (
              <div
                key={modulo.id}
                className="bg-white border border-border/40 rounded-xl shadow-sm overflow-hidden"
                data-testid={`module-card-${modulo.id}`}
              >
                {/* Cabecera del módulo */}
                <div 
                  className="p-4 md:p-6 flex items-center justify-between cursor-pointer hover:bg-zinc-50 transition-colors"
                  onClick={() => setExpandedModule(isExpanded ? null : modulo.id)}
                  data-testid={`module-header-${modulo.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${modulo.color} flex items-center justify-center`}>
                      <Icono className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-medium text-black text-lg">{modulo.nombre}</h3>
                      <p className="text-sm text-muted-foreground">{modulo.descripcion}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Contador */}
                    <div className="text-right hidden sm:block">
                      <p className="text-2xl font-semibold text-black">
                        {loadingCounts ? '...' : count.toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">registros</p>
                    </div>
                    
                    {/* Botón expandir */}
                    <div className={`w-8 h-8 rounded-full ${modulo.colorLight} flex items-center justify-center transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                      <ChevronRight className="w-5 h-5 text-black" />
                    </div>
                  </div>
                </div>
                
                {/* Filtros expandidos */}
                {isExpanded && (
                  <div className="border-t border-border/40 px-4 md:px-6 pb-6">
                    {renderFiltrosModulo(modulo)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Link a Exportación Ejecutiva */}
        <div className="mt-8 p-6 bg-gradient-to-r from-[#1a365d] to-[#2d4a7c] rounded-xl text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
                <FileSpreadsheet className="w-7 h-7 text-gold" />
              </div>
              <div>
                <h3 className="font-serif text-xl">Exportación Ejecutiva JPM</h3>
                <p className="text-white/70 text-sm">Reporte premium consolidado con toda la información cruzada</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/export/executive')}
              className="flex items-center gap-2 px-6 py-3 bg-gold text-black rounded-lg text-xs uppercase tracking-wider font-medium hover:bg-gold-light transition-colors"
            >
              Ir a Exportación Ejecutiva
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExportModules;
